/**
 * "Reviews and comments" gadget (Team): the open comment threads on the project's files (vfs-service
 * `/collab/comments`, the threads Team ▾ › Notes › Comments lists), the suggestions waiting for a decision (`/collab/suggestions`)
 * and the handoff notes others left that you have not dismissed (`/collab/handoffs/unseen`). Each opens where it is
 * handled: Notes on Comments (`flow:open-comments`, Canvas), the suggestion's review, the note. Each list that could
 * not be read says so on its own.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { ago } from '../../../utils/blameModel';
import { reviewSummary } from '../../../utils/homeGadgets/team';
import GadgetCard, { TabLink } from './GadgetCard';
import { useComments, useSuggestions, useUnseenHandoffs } from './sources';

const SHOWN = 3;
const fileOf = (path) => String(path || '').split('/').pop() || 'the project';

/** Opens Team ▾ › Notes on Comments (Canvas listens). */
export const openComments = () => window.dispatchEvent(new CustomEvent('flow:open-comments'));

export default function ReviewsGadget({ projectId, onOpenComments = openComments, onOpenSuggestion = null, onOpenHandoff = null }) {
  const comments = useComments(projectId);
  const suggestions = useSuggestions(projectId);
  const handoffs = useUnseenHandoffs(projectId);
  const summary = reviewSummary({ threads: comments.data?.threads || (comments.data ? [] : null), suggestions: suggestions.data?.suggestions || (suggestions.data ? [] : null), handoffs: handoffs.data?.notes || (handoffs.data ? [] : null) });
  const loading = (comments.loading && !comments.data) || (suggestions.loading && !suggestions.data) || (handoffs.loading && !handoffs.data);
  const allFailed = comments.error && !comments.data && suggestions.error && !suggestions.data && handoffs.error && !handoffs.data;
  const count = (summary.threads?.length || 0) + (summary.suggestions?.length || 0) + (summary.handoffs?.length || 0);
  let body;
  if (allFailed) { body = <p className="ph-quiet" data-testid="gadget-error">The comments and reviews could not be read just now.</p>; }
  else if (count === 0 && loading) { body = <p className="ph-quiet" data-testid="gadget-loading">Reading the comments and reviews…</p>; }
  else if (count === 0 && !comments.error && !suggestions.error && !handoffs.error) { body = <p className="ph-quiet" data-testid="gadget-empty">No open comment, no suggestion waiting and no note for you.</p>; }
  else {
    body = (
      <>
        <ul className="gd-rows">
          {(summary.handoffs || []).slice(0, SHOWN).map((note) => (
            <li className="gd-row" data-testid={`review-note-${note.id}`} key={`n-${note.id}`}>
              <span className="gd-warn">Note</span>
              <span className="gd-grow">{note.by?.name || 'Someone'} {note.forYou ? 'left you a note' : 'left a note'} <span className="ph-muted ph-small">{ago(note.at)}</span></span>
              <TabLink onClick={onOpenHandoff ? () => onOpenHandoff(note.id) : null}>Read</TabLink>
            </li>
          ))}
          {(summary.suggestions || []).slice(0, SHOWN).map((s) => (
            <li className="gd-row" data-testid={`review-suggestion-${s.id}`} key={`s-${s.id}`}>
              <span className="gd-warn">Suggestion</span>
              <span className="gd-grow">{s.by?.name || 'Someone'} on {fileOf(s.path)} <span className="ph-muted ph-small">{ago(s.at)}</span></span>
              <TabLink onClick={onOpenSuggestion ? () => onOpenSuggestion(s.id) : null}>Review</TabLink>
            </li>
          ))}
          {(summary.threads || []).slice(0, SHOWN).map((t) => (
            <li className="gd-row" data-testid={`review-thread-${t.id}`} key={`t-${t.id}`}>
              <span className="ph-muted">Comment</span>
              <span className="gd-grow">{fileOf(t.path)}{Number.isInteger(t.line) ? `:${t.line}` : ''} · {String(t.body || '').slice(0, 80)}</span>
              {(t.replies || []).length > 0 && <span className="ph-muted ph-small">{t.replies.length} {t.replies.length === 1 ? 'reply' : 'replies'}</span>}
            </li>
          ))}
        </ul>
        {comments.error && !comments.data && <p className="gd-note">The comments could not be read just now.</p>}
        {suggestions.error && !suggestions.data && <p className="gd-note">The suggestions could not be read just now.</p>}
        {handoffs.error && !handoffs.data && <p className="gd-note">The handoff notes could not be read just now.</p>}
      </>
    );
  }
  const meta = [
    summary.threads ? `${summary.threads.length} open` : null,
    summary.suggestions?.length ? `${summary.suggestions.length} waiting` : null,
  ].filter(Boolean).join(' · ');
  return (
    <GadgetCard links={<TabLink onClick={onOpenComments}>Comments</TabLink>} meta={meta || null} testId="reviews-gadget" title="Reviews and comments">
      {body}
    </GadgetCard>
  );
}

ReviewsGadget.propTypes = {
  onOpenComments: PropTypes.func,
  onOpenHandoff: PropTypes.func,
  onOpenSuggestion: PropTypes.func,
  projectId: PropTypes.string.isRequired,
};
